import { configureStore } from "@reduxjs/toolkit";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { currentStudentSlice } from "./slices/current-student.slice";
import { registerFormSlice } from "./slices/register-form.slice";
import conversationSlice from "./slices/conversation.slice";
import menuGroupsSlice from "./slices/menu-groups.slice";
import Cookies from "js-cookie";

export type SidebarSettings = {
  disabled: boolean;
  isHoverOpen: boolean;
};

interface SidebarState {
  isOpen: boolean;
  isHover: boolean;
  settings: SidebarSettings;
}

type Theme = "dark" | "light" | "system";
type Language = "fr" | "en";

interface PreferencesState {
  theme: Theme;
  language: Language;
  shouldFilterExamsByUserLevel: boolean;
}

const getInitialPreferences = (): PreferencesState => {
  return {
    theme: (Cookies.get("theme") as Theme) || "system",
    language: (Cookies.get("language") as Language) || "fr",
    shouldFilterExamsByUserLevel: Cookies.get("shouldFilterExamsByUserLevel")
      ? Cookies.get("shouldFilterExamsByUserLevel") === "true"
      : true,
  };
};

const getInitialSidebarState = (): SidebarState => {
  return {
    isOpen: Cookies.get("sidebarOpen")
      ? Cookies.get("sidebarOpen") === "true"
      : true,
    isHover: false,
    settings: { disabled: false, isHoverOpen: false },
  };
};

const preferencesSlice = createSlice({
  name: "preferences",
  initialState: getInitialPreferences(),
  reducers: {
    setTheme: (state, action: PayloadAction<Theme>) => {
      state.theme = action.payload;
      Cookies.set("theme", action.payload);
    },
    setLanguage: (state, action: PayloadAction<Language>) => {
      state.language = action.payload;
      Cookies.set("language", action.payload);
    },
    setShouldFilterExamsByUserLevel: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.shouldFilterExamsByUserLevel = action.payload;
      Cookies.set("shouldFilterExamsByUserLevel", action.payload.toString());
    },
  },
});

const sidebarSlice = createSlice({
  name: "sidebar",
  initialState: getInitialSidebarState(),
  reducers: {
    toggleOpen: (state) => {
      state.isOpen = !state.isOpen;
      Cookies.set("sidebarOpen", state.isOpen.toString());
    },
    setIsOpen: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload;
      Cookies.set("sidebarOpen", action.payload.toString());
    },
    setIsHover: (state, action: PayloadAction<boolean>) => {
      state.isHover = action.payload;
    },
    setSettings: (state, action: PayloadAction<Partial<SidebarSettings>>) => {
      state.settings = { ...state.settings, ...action.payload };
    },
  },
});

export const { toggleOpen, setIsOpen, setIsHover, setSettings } =
  sidebarSlice.actions;

export const { setTheme, setLanguage, setShouldFilterExamsByUserLevel } =
  preferencesSlice.actions;

export const store = configureStore({
  reducer: {
    sidebar: sidebarSlice.reducer,
    currentStudent: currentStudentSlice.reducer,
    registerForm: registerFormSlice.reducer,
    preferences: preferencesSlice.reducer,
    conversation: conversationSlice.reducer,
    menuGroups: menuGroupsSlice,
  },
});

export const selectSidebarOpenState = (state: RootState) =>
  state.sidebar.isOpen ||
  (state.sidebar.settings.isHoverOpen && state.sidebar.isHover);

export const selectTheme = (state: RootState) => state.preferences.theme;
export const selectLanguage = (state: RootState) => state.preferences.language;
export const selectShouldFilterExamsByUserLevel = (state: RootState) =>
  state.preferences.shouldFilterExamsByUserLevel;

export const selectMenuGroups = (state: RootState) => state.menuGroups.collapsedGroups;

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
